import { MAX_INT64_DIGITS, MAX_INT128_DIGITS } from './decimalScale';

/**
 * Kako se tip kolone PIŠE korisniku.
 *
 * Kolona može imati fizički tip u `type` i logički u `logical_type`: tačna
 * decimala je fizički `int64_t`, a logički `decimal(12,2)`. Fizički tip je ono
 * što je zapisano na lancu i što čvor indeksira; logički je ono što vrednost
 * znači. Korisniku treba drugo — `int64_t` uz kolonu `valor` ne kaže ništa, a
 * `decimal(12,2)` kaže sve.
 *
 * Kolona bez `logical_type` se ponaša kao i pre.
 */
export const displayType = (field) => {
    if (!field) return '';

    if (field.logical_type === 'decimal' && Number.isInteger(field.scale)) {
        return Number.isInteger(field.precision)
            ? `decimal(${field.precision},${field.scale})`
            : `decimal(,${field.scale})`;
    }

    if (field.is_object) return field.object?.name || field.type;
    return field.type;
};

/**
 * Kratka napomena uz decimalnu kolonu — ono što korisnik mora znati ako čita
 * lanac mimo GUI-ja.
 */
export const storageNote = (field) => {
    if (field?.logical_type !== 'decimal' || !Number.isInteger(field.scale)) return '';
    return `stored as ${field.type}, scaled by 10^${field.scale}`;
};

/**
 * Rečnik ČAROBNJAKA → rečnik LANCA.
 *
 * Korisnik bira `decimal` i zada broj cifara; na lanac ide `int64_t`, jer takav
 * tip tamo i postoji. Logički tip ostaje uz njega u `db.struct`, gde ga čita
 * samo naš backend — `schemas` i CDT o njemu ne znaju ništa.
 *
 * Pretvaranje je namerno OVDE, na granici pred slanje, a ne u samom obrascu:
 * dok korisnik uređuje kolonu ona je `decimal`, jer to i jeste.
 */
// Koliko cifara kolona sme da traži — izvedeno iz onoga što fizički tip nosi,
// a ne prepisano. `int128` je prohodao 26.08.2026, pa je granica pomerena sa 18
// na 38; ovo je bilo to „jedno mesto".
export const MAX_DECIMAL_PRECISION = MAX_INT128_DIGITS;

/**
 * Fizički tip za zadat broj cifara.
 *
 * IZVODI se, ne pamti. Čarobnjak decimalu drži kao `type: "decimal"` i pri tom
 * odbacuje fizički tip (vidi `toWizardField`) — pa kad je vraćao, `toWireField`
 * je tvrdo upisivao `int64_t`. Kolona koju je Mongo uvoz prepoznao kao
 * `decimal(24,2)` tako je na lanac odlazila kao `int64_t`, a uvoz padao na
 * „Value 999999999999999999999999 does not fit int64_t".
 *
 * Isto pravilo koristi i `mongo/schema.js`, pa se dve strane ne mogu razići.
 */
export const physicalTypeFor = (precision) =>
    Number(precision) <= MAX_INT64_DIGITS ? 'int64_t' : 'int128_t';

export const toWireField = (field) => {
    if (!field || field.type !== 'decimal') return field;

    const precision = Number.isInteger(Number(field.precision))
        ? Number(field.precision)
        : MAX_DECIMAL_PRECISION;
    const scale = Number.isInteger(Number(field.scale)) ? Number(field.scale) : 0;

    return {
        ...field,
        // Fizički tip prati broj cifara — vidi `physicalTypeFor`.
        type: physicalTypeFor(precision),
        logical_type: 'decimal',
        precision,
        scale,
    };
};

/**
 * Rečnik LANCA → rečnik ČAROBNJAKA. Obrnuto od `toWireField`.
 *
 * Bez ovoga kolona koju je uvoz prepoznao kao decimalnu stiže u čarobnjak sa
 * `type: "int64_t"` i tamo izgleda kao običan ceo broj — onaj ko uvozi ne vidi
 * da ima decimale niti može da ih promeni. Ista komponenta uređuje i ručno
 * napravljene i uvezene kolone, pa mora da dobije JEDAN rečnik.
 *
 * `logical_type` se pri tom SKIDA: u čarobnjaku značenje nosi sam `type`, a
 * ostavljena oznaka bi preživela promenu tipa i kolona bi ostala „decimal" i
 * pošto je korisnik prebaci u nešto drugo. Vraća ga `toWireField`.
 */
export const toWizardField = (field) => {
    if (!field || field.logical_type !== 'decimal') return field;
    const { logical_type, ...rest } = field;
    return { ...rest, type: 'decimal' };
};

export const toWireComposition = (composition) => ({
    ...composition,
    fields: (composition?.fields || []).map(toWireField),
    indexes: (composition?.indexes || []).map(toWireField),
});

/**
 * Šta ne valja sa zadatom decimalnom kolonom, ili `null` ako je u redu.
 * Poruka ide korisniku, pa govori o ciframa a ne o tipovima.
 */
export const decimalProblem = (field) => {
    if (!field || field.type !== 'decimal') return null;
    const p = Number(field.precision);
    const s = Number(field.scale);
    if (!Number.isInteger(p) || p < 1) return 'Total digits must be a whole number of at least 1.';
    if (p > MAX_DECIMAL_PRECISION) return `At most ${MAX_DECIMAL_PRECISION} digits fit an exact column.`;
    if (!Number.isInteger(s) || s < 0) return 'Decimal places must be a whole number, zero or more.';
    if (s > p) return 'Decimal places cannot exceed the total number of digits.';
    return null;
};
